import Backbone from 'backbone';
import $ from 'jquery';

import asm from 'assembly/Asm';
import {assemblies, Assembly} from 'assembly/AssemblyModels';
import AssemblyTabSettingsModal from 'assembly/tabs/AssemblyTabSettingsModal';
import PrimerAssemblyTabState, {
  PrimerAssemblyTabRowState,
} from 'assembly/tabs/primer-assembly/backbone/PrimerAssemblyTabState';
import PrimerAssemblyTabView from 'assembly/tabs/primer-assembly/backbone/PrimerAssemblyTabView';
import PrimerAssemblyTabOptionsTemplate from 'assembly/tabs/primer-assembly/templates/PrimerAssemblyTabOptionsTemplate';
import {NucleotideType} from 'biojs/BioConstants';
import {FragmentResult} from 'biojs/FragmentResult';
import {ASSEMBLY_TYPE} from 'constants/AssemblyConstants';
import {SequenceSummary} from 'constants/SequenceConstants';
import {
  downloadOligos,
  formatOligos,
  getOligoFilename,
  PRIMERS_COPIED_MSG,
} from 'molbio/oligos/ExportOligoUtil';
import notifications from 'notifications';
import legacyRunModal from 'ui-library/legacyRunModal';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import * as clipboard from 'util/Clipboard';
import * as logging from 'util/Logging';

export default class OptionsView extends Backbone.View<PrimerAssemblyTabState> {
  clipboard?: clipboard.ClipboardProxy;
  template!: () => string;
  selectorView?: Backbone.View<PrimerAssemblyTabState>;

  static initClass(): void {
    this.prototype.template = PrimerAssemblyTabOptionsTemplate;
    this.prototype.className = 'navbar';

    this.prototype.events = {
      // @ts-expect-error: does not match vendored types
      'click .js-print': 'print',
      'click .js-save': 'save',
      'click .js-clone-into-wizard': 'cloneIntoWizard',
      'click .js-primer-tab-settings': function () {
        legacyRunModal(AssemblyTabSettingsModal, {});
      },
    };
  }

  override initialize(): void {
    this.listenTo(
      this.model.get('sequence'),
      'change:writable change:permissions change:currentVersion',
      () => {
        this.renderFinalize();
      }
    );
    this.listenTo(this.model.get('sequence')._sequence(), 'change:finalized', () => {
      this.renderFinalize();
    });
    this.listenTo(this.model.get('rows'), 'reset', () => {
      this.renderCopyButton();
      this.renderExportButton();
    });
    this.listenTo(this.model, 'print', () => {
      this.print();
    });
  }

  override remove(): this {
    super.remove();
    if (this.selectorView != null) {
      this.selectorView.remove();
    }
    if (this.clipboard != null) {
      clipboard.unglue(this.clipboard);
    }
    return this;
  }

  print(): void {
    LoadingScreen.show();
    const sequence = this.model.get('sequence')._sequence();
    const showSummary = (): void => {
      const printWindow = window.open();
      if (printWindow == null) {
        // This block should never be reached, but we deal with it instead of erroring in case
        // browsers introduce some weird behavior.
        logging.warn('Assembly tab blocked');
        notifications.warn('Please disable popup blockers and try again in a few seconds.');
        this.model.set({showPrintableSummary: false});
        LoadingScreen.hide();
        return;
      }
      const $printDocument = $(printWindow.document);
      $printDocument.find('head').append('<link rel="stylesheet" href="/static/build/benchling.css"/>');
      $printDocument
        .find('head')
        .append('<link href="https://fonts.googleapis.com/css?family=Roboto" rel="stylesheet"/>');
      $printDocument.find('head').append('<link rel="icon" type="image/x-icon" href="/static/favicon.ico"/>');
      $printDocument.find('head').append('<link rel="mask-icon" href="/static/navy_jeffyp.svg"/>');
      $printDocument.find('body').append('<div class="printable-summary-container" />');
      // Backbone just types this as `Model` oy vey
      const printableSummaryModel = this.model.clone() as PrimerAssemblyTabState;
      // A `clone` only goes one layer deep. Clone the rows as well; we need their dG / mispriming values
      const rows = this.model.get('rows').clone();
      printableSummaryModel.set({showPrintableSummary: true, rows});
      const tabView = new PrimerAssemblyTabView({model: printableSummaryModel});
      tabView._renderTab($printDocument.find('body').find('.printable-summary-container')[0]);
      printWindow.focus();
      // It can take a bit for the sequence preview SVG to load
      setTimeout(() => {
        printWindow.print();
      }, 1000);
      LoadingScreen.hide();
    };
    if (sequence.get('hasSnapshot')) {
      showSummary();
    } else {
      // This call must be synchronous because the browser pops blockups that are generated from
      // events not triggered directly by the user. Chaining these synchronous calls appears to
      // involve the popup blocker as well, as does deferring anything.
      $.ajax({
        url: `/1/api/sequences/${sequence.id}`,
        async: false,
        dataType: 'json',
        success(res: SequenceSummary) {
          if (res.hasSnapshot) {
            sequence.set({snapshot_url: res.snapshot_url, hasSnapshot: true});
            showSummary();
          } else {
            notifications.warn('Sequence preview is not available yet. Please try again in a few seconds.');
            LoadingScreen.hide();
          }
        },
      });
    }
  }

  save(): void {
    this.$('.js-save').addClass('disabled');
    const params: {
      fragmentResultStickyEnds?: {
        [id: string]: {start?: string; end?: never} | {start?: never; end?: string};
      };
    } = {};
    if (this.model.get('sequence').assemblyType() === ASSEMBLY_TYPE.GOLDEN_GATE) {
      this.model
        .get('sequence')
        .fragmentResults()
        .each((fragmentResult: FragmentResult) => {
          params.fragmentResultStickyEnds ??= {};
          params.fragmentResultStickyEnds[fragmentResult.id] = {};
        });
      const stickyEndRanges = asm.goldenGate.stickyEndRanges(this.model.get('sequence')._sequence());
      for (const {start, end, upstreamFragment, downstreamFragment} of stickyEndRanges) {
        const stickyEnd = this.model.get('sequence').residueSlice(start, end);
        params.fragmentResultStickyEnds ??= {};
        params.fragmentResultStickyEnds[upstreamFragment.id].end = stickyEnd;
        params.fragmentResultStickyEnds[downstreamFragment.id].start = stickyEnd;
      }
    }
    this.model
      .get('sequence')
      .finalize(params)
      .always(() => this.$('.js-save').removeClass('disabled'))
      .fail(notifications.notifyError);
  }

  renderFinalize(): void {
    const hideFinalize =
      this.model.get('sequence')._sequence().finalized() || !this.model.get('sequence').allowFeatureEdits();
    this.$('.js-finalize-group').toggleClass('hidden', hideFinalize);
  }

  cloneIntoWizard(): void {
    LoadingScreen.show();
    const toClone = this.model.get('sequence')._sequence().assembly()!;
    toClone
      .backendClone()
      .done((assembly: Assembly) => {
        assemblies.add(assembly);
        if (toClone.allFragments().length !== assembly.allFragments().length) {
          notifications.warn(
            `Some sequences that were originally used in this assembly have been shortened \
or deleted. You will need to re-set those fragments.`,
            {duration: 10}
          );
        }
      })
      .fail(notifications.notifyError)
      .always(() => {
        LoadingScreen.hide();
      });
  }

  canExportPrimers(): boolean {
    return window.user != null;
  } // must be logged in

  primersToExport(): Array<{name: string; nucleotideType: NucleotideType; bases: string}> {
    return this.model.get('rows').map((row: PrimerAssemblyTabRowState) => {
      const primer = this.model.primers.get(row.id);
      return {name: primer.name(), nucleotideType: primer.nucleotideType(), bases: primer.bases()};
    });
  }

  renderCopyButton(): void {
    const $btn = this.$('.js-copy-primers');
    if (!this.canExportPrimers()) {
      $btn.addClass('hidden');
      return;
    }
    $btn.removeClass('hidden');
    const primersToExport = this.primersToExport();
    $btn.toggleClass('disabled', primersToExport.length === 0);
    if (this.clipboard != null) {
      clipboard.unglue(this.clipboard);
    }
    const primerData = formatOligos(primersToExport, 'tsv');
    this.clipboard = clipboard.glue($btn[0], {
      dataTransform() {
        return primerData;
      },
      notifyCopied() {
        notifications.success(PRIMERS_COPIED_MSG, {
          duration: 30,
          id: 'tsv-primer-copied',
        });
      },
    });
  }

  renderExportButton(): void {
    const $btn = this.$('.js-export-group .button');
    if (!this.canExportPrimers()) {
      $btn.addClass('hidden');
      return;
    }
    $btn.removeClass('hidden');
    const primersToExport = this.primersToExport();
    const disabled = primersToExport.length === 0;
    $btn.toggleClass('is-disabled', disabled);
    if (primersToExport.length >= 1) {
      $btn.click(() => {
        if (disabled) {
          return;
        }
        downloadOligos({
          oligos: primersToExport,
          filename: getOligoFilename(this.model.get('sequence').name()),
        });
      });
    }
  }

  override render(): this {
    this.$('.js-print').tooltip('destroy');
    this.$el.html(this.template());
    this.$('.js-print').tooltip({title: 'Printable Summary', placement: 'bottom', container: 'body'});
    this.$('.js-clone-into-wizard').tooltip({
      placement: 'bottom',
      title: 'Open a copy of this assembly in the assembly wizard',
      container: 'body',
      trigger: 'hover',
    });
    this.$('.js-save').tooltip({
      placement: 'bottom',
      title: `\
Finalize the assembly to enable creating alignments and editing anywhere in the sequence\
`,
      trigger: 'hover',
      container: 'body',
    });
    this.renderExportButton();
    this.renderFinalize();
    this.renderCopyButton();
    return this;
  }
}

OptionsView.initClass();
